"use client";

import { Quote } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
export default function Testimonials() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: carouselRef,
    isVisible: carouselVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const testimonials = [{
    quote: "NoMoCo products allow us to comfortably secure our subjects' heads for scanning, and greatly reduce the amount of data lost to motion artifacts. More importantly, the pillows increase comfort and encourage subjects to adopt a relaxed posture when participating in functional MRI scans. The various pillows also help to accommodate different head shapes and sizes.",
    name: "Lisa Marie Langevin PhD",
    role: "Alberta Children's Hospital Research Institute"
  }, {
    quote: "We have used the NoMoCo pillow for our Diffusion Tensor Imaging experiments at the Radiology Imaging Laboratories. We started our project without the pillows and noticed patient discomfort associated with table vibration due to specifics of our imaging protocol. We acquired the NoMoCo pillow and there was considerable patient satisfaction and improvement in comfort. In addition, support and follow up from the vendor have been exceptional, with prompt and professional response and resolution of any questions or issues.",
    name: "Mazyar E. Ahmadi MD",
    role: "UC San Diego Radiology"
  }, {
    quote: "I have used the NoMoCo pillow to help increase comfort and reduce motion in child, adolescent, and young adult research participants. Comfort and motion are critical design concerns with youth studies. After trying other, less effective techniques, I happily settled on the NoMoCo pillow system. Since then, not only have we seen less motion in our imaging data, but also noticeable improvements in participant comfort with scanning.",
    name: "Susan Tapert PhD",
    role: "UC San Diego Psychiatry"
  }, {
    quote: "We conduct sleep deprivation studies in older and younger adults. Subject motion in older populations, and in anyone who is sleep deprived, can be a serious problem. We have found that the comfort and stability afforded by the NoMoCo system have dramatically reduced the amount of data lost to excessive motion. In addition, the subjects consistently comment that the pillows are comfortable and our staff find them very easy to use.",
    name: "Sean P.A. Drummond PhD",
    role: "UCSD Lab of Sleep & Behavioral Neuroscience"
  }];
  return <section data-section-id="14383" id="researchtestimonials" className="bg-background border-b-2 border-border !p-0">
      <div className="mx-auto max-w-[1320px] px-5 md:px-8">
        {}
        <div ref={headerRef} className={`grid grid-cols-12 gap-6 mb-16 transition-all duration-500 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <div className="col-span-12 text-center">
            <div className="font-default text-xs font-semibold uppercase tracking-[0.14em] mb-6 text-accent">
              {"// CLINICAL FEEDBACK"}
            </div>
            <h2 className="font-default leading-[0.96] tracking-[-0.03em] text-[clamp(2.25rem,4.5vw,4rem)] text-primary !font-bold">
              Trusted by professionals.
            </h2>
          </div>
        </div>

        {}
        <div ref={carouselRef} className={`transition-all duration-700 ease-out ${carouselVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Carousel opts={{
          align: "start",
          loop: true
        }} className="w-full max-w-5xl mx-auto">
            <CarouselContent>
              {testimonials.map((item, i) => <CarouselItem key={i} data-index={i}>
                  <div className="flex flex-col items-center text-center ![padding:0px_16px_0px_16px]">
                    <div className="mb-8">
                      <Quote className="text-primary/20" strokeWidth={2} style={{
                    width: "4rem",
                    height: "4rem"
                  }} aria-hidden="true" />
                    </div>

                    <blockquote className="font-default text-foreground font-medium leading-[1.4] tracking-tight mb-10 max-w-4xl !text-2xl !italic">
                      "{item.quote}"
                    </blockquote>

                    <div className="flex flex-col items-center">
                      <div className="h-px w-12 bg-primary mb-6" />
                      <cite className="not-italic">
                        <span className="block font-default text-foreground font-bold text-lg md:text-xl">
                          {item.name}
                        </span>
                        <span className="block font-default text-muted-foreground text-sm md:text-base mt-2 uppercase tracking-wider">
                          {item.role}
                        </span>
                      </cite>
                    </div>
                  </div>
                </CarouselItem>)}
            </CarouselContent>
            <div className="hidden md:block">
              <CarouselPrevious className="left-[-60px] border-2 border-border hover:bg-accent hover:text-primary transition-colors rounded-lg" />
              <CarouselNext className="right-[-60px] border-2 border-border hover:bg-accent hover:text-primary transition-colors rounded-lg" />
            </div>
          </Carousel>
        </div>
      </div>
    </section>;
}